import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { Navbar } from "@/components/Navbar";
import { SiteFooter } from "@/components/SiteFooter";
import { VehicleCard } from "@/components/VehicleCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import api from "@/lib/api";
import { currency } from "@/lib/status";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Search, ShieldCheck, Store, SlidersHorizontal, X, Sparkles, Share2, BookmarkPlus, Car, CarFront, Truck, Caravan, Tag } from "lucide-react";
import { useContent, INVENTORY_DEFAULTS } from "@/lib/content";
import { promoForVehicle } from "@/lib/promotions";
import { PromotionBannerCarousel } from "@/components/PromotionBannerCarousel";
import { FeaturedTextPromotions } from "@/components/FeaturedTextPromotions";

const BODY_ICON = { SUV: Car, Sedan: CarFront, Truck: Truck, Coupe: CarFront, Convertible: CarFront, Hatchback: Car, Wagon: Car, Van: Caravan, Minivan: Caravan, Other: Car };

const COLOR_HEX = { White: "#f1f5f9", Black: "#0f172a", Silver: "#cbd5e1", Gray: "#94a3b8", Blue: "#2563eb", Red: "#dc2626", Green: "#16a34a", Brown: "#78350f", Beige: "#d6c7a1", Gold: "#d4af37", Orange: "#ea580c", Yellow: "#eab308", Purple: "#7c3aed", Other: "#e2e8f0" };

const EMPTY = { q: "", makes: [], models: [], trims: [], colors: [], bodies: [], yearMin: null, yearMax: null, priceMin: null, priceMax: null, verified: false, dealerListed: false, sort: "newest" };

const RangeSlider = ({ min, max, step, value, onValueChange, onValueCommit }) => (
  <SliderPrimitive.Root className="relative flex w-full touch-none select-none items-center py-2" min={min} max={max} step={step} value={value} onValueChange={onValueChange} onValueCommit={onValueCommit} minStepsBetweenThumbs={0}>
    <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"><SliderPrimitive.Range className="absolute h-full bg-rose-600" /></SliderPrimitive.Track>
    <SliderPrimitive.Thumb className="block h-4 w-4 rounded-full border-2 border-rose-600 bg-white dark:bg-slate-900 shadow focus:outline-none focus:ring-2 focus:ring-rose-200" />
    <SliderPrimitive.Thumb className="block h-4 w-4 rounded-full border-2 border-rose-600 bg-white dark:bg-slate-900 shadow focus:outline-none focus:ring-2 focus:ring-rose-200" />
  </SliderPrimitive.Root>
);

function parseParams(sp) {
  const n = (k) => (sp.get(k) ? Number(sp.get(k)) : null);
  return {
    q: sp.get("q") || "", makes: sp.getAll("make"), models: sp.getAll("model"), trims: sp.getAll("trim"),
    colors: sp.getAll("color"), bodies: sp.getAll("body"), yearMin: n("year_min"), yearMax: n("year_max"),
    priceMin: n("price_min"), priceMax: n("price_max"), verified: sp.get("verified_only") === "true",
    dealerListed: sp.get("dealer_listed") === "true", sort: sp.get("sort") || "newest",
  };
}

function toQS(f) {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  f.makes.forEach((x) => p.append("make", x));
  f.models.forEach((x) => p.append("model", x));
  f.trims.forEach((x) => p.append("trim", x));
  f.colors.forEach((x) => p.append("color", x));
  f.bodies.forEach((x) => p.append("body", x));
  if (f.yearMin != null) p.set("year_min", f.yearMin);
  if (f.yearMax != null) p.set("year_max", f.yearMax);
  if (f.priceMin != null) p.set("price_min", f.priceMin);
  if (f.priceMax != null) p.set("price_max", f.priceMax);
  if (f.verified) p.set("verified_only", "true");
  if (f.dealerListed) p.set("dealer_listed", "true");
  if (f.sort && f.sort !== "newest") p.set("sort", f.sort);
  return p.toString();
}

export default function Inventory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [filters, setFilters] = useState(() => parseParams(sp));
  const [vehicles, setVehicles] = useState(null);
  const [facets, setFacets] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [makeQuery, setMakeQuery] = useState("");
  const [qInput, setQInput] = useState(filters.q);
  const [matchedIds, setMatchedIds] = useState(() => new Set());
  const [promos, setPromos] = useState([]);
  const [specials, setSpecials] = useState(false);
  const c = useContent("inventory", INVENTORY_DEFAULTS);
  const bounds = useRef(null);
  const qs = useMemo(() => toQS(filters), [filters]);

  useEffect(() => {
    const t = setTimeout(() => set({ q: qInput }), 300);
    return () => clearTimeout(t);
  }, [qInput]); // eslint-disable-line

  useEffect(() => {
    setSp(qs, { replace: true });
    setVehicles(null);
    let live = true;
    Promise.all([api.get(`/vehicles?${qs}`), api.get(`/vehicles/facets?${qs}`)]).then(([v, fc]) => {
      if (!live) return;
      setVehicles(v.data);
      setFacets(fc.data);
      if (!bounds.current && fc.data.year.min != null) bounds.current = { year: fc.data.year, price: fc.data.price };
    }).catch(() => live && setVehicles([]));
    return () => { live = false; };
  }, [qs]); // eslint-disable-line

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const toggle = (key, val) => setFilters((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));
  const clearAll = () => { setQInput(""); setFilters(EMPTY); };

  const shareResults = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied — share these results anywhere"); }
    catch { toast.error("Could not copy link"); }
  };
  const saveSearch = async () => {
    const name = window.prompt("Name this search (e.g. 'White Audi SUV under $60k')");
    if (name === null) return;
    try { await api.post("/saved-searches", { name: name || "My search", query_string: qs }); toast.success("Search saved — we'll email you when a match lands"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save search"); }
  };
  const isBuyer = user && !["super_admin", "broker", "sales", "staff", "admin", "partner"].includes(user.role);

  useEffect(() => {
    if (!isBuyer) { setMatchedIds(new Set()); return; }
    let live = true;
    api.get("/saved-searches/matches").then(({ data }) => { if (live) setMatchedIds(new Set(data.ids || [])); }).catch(() => {});
    return () => { live = false; };
  }, [isBuyer, vehicles]); // eslint-disable-line

  useEffect(() => {
    api.get("/promotions/active").then(({ data }) => setPromos(data || [])).catch(() => {});
  }, []);

  const yb = bounds.current?.year || facets?.year || { min: 2015, max: 2026 };
  const pb = bounds.current?.price || facets?.price || { min: 0, max: 200000 };
  const yearVal = [filters.yearMin ?? yb.min, filters.yearMax ?? yb.max];
  const priceVal = [filters.priceMin ?? pb.min, filters.priceMax ?? pb.max];

  const activeChips = [
    ...filters.makes.map((v) => ({ k: "makes", v, label: v })),
    ...filters.models.map((v) => ({ k: "models", v, label: v })),
    ...filters.trims.map((v) => ({ k: "trims", v, label: v })),
    ...filters.bodies.map((v) => ({ k: "bodies", v, label: v })),
    ...filters.colors.map((v) => ({ k: "colors", v, label: v })),
  ];
  const anyActive = activeChips.length || filters.verified || filters.dealerListed || filters.yearMin != null || filters.priceMin != null || filters.q;
  const promoCount = vehicles ? vehicles.filter((v) => promoForVehicle(promos, v)).length : 0;
  const shown = vehicles ? (specials ? vehicles.filter((v) => promoForVehicle(promos, v)) : vehicles) : null;

  const FacetGroup = ({ title, options, keyName, prefix }) => (
    <div className="border-b border-slate-100 dark:border-slate-800 py-4">
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</p>
      <div className="max-h-44 space-y-1 overflow-y-auto pr-1">
        {options.length === 0 && <p className="text-xs text-slate-400 dark:text-slate-500">None available</p>}
        {options.map((o) => {
          const checked = filters[keyName].includes(o.value);
          return (
            <button key={o.value} onClick={() => toggle(keyName, o.value)} data-testid={`${prefix}filter-${keyName}-${String(o.value).toLowerCase().replace(/\s/g, "-")}`}
              className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm transition-colors ${checked ? "bg-rose-50 font-semibold text-rose-600" : "text-slate-700 dark:text-slate-300 hover:bg-slate-100"}`}>
              <span className="flex items-center gap-2">
                <span className={`flex h-4 w-4 items-center justify-center rounded border ${checked ? "border-rose-600 bg-rose-600" : "border-slate-300 dark:border-slate-700"}`}>{checked && <span className="h-1.5 w-1.5 rounded-sm bg-white dark:bg-slate-900" />}</span>
                {o.value}
              </span>
              <span className="text-xs text-slate-400 dark:text-slate-500">{o.count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderFilters = (prefix = "") => {
    if (!facets) return <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>;
    const makeOpts = facets.makes.filter((m) => m.value.toLowerCase().includes(makeQuery.toLowerCase()));
    return (
      <div>
        <div className="flex flex-col gap-2 pb-3">
          <button onClick={() => set({ verified: !filters.verified })} data-testid={`${prefix}toggle-verified`}
            className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${filters.verified ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50"}`}>
            <ShieldCheck className="h-4 w-4" /> Broker Verified only
          </button>
          <button onClick={() => set({ dealerListed: !filters.dealerListed })} data-testid={`${prefix}toggle-dealer`}
            className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${filters.dealerListed ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50"}`}>
            <Store className="h-4 w-4" /> Dealer Listed
          </button>
        </div>

        <div className="border-b border-slate-100 dark:border-slate-800 py-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Make</p>
          {facets.makes.length > 7 && <Input value={makeQuery} onChange={(e) => setMakeQuery(e.target.value)} placeholder="Search brands" className="mb-2 h-8" data-testid={`${prefix}make-search`} />}
          <div className="max-h-44 space-y-1 overflow-y-auto pr-1">
            {makeOpts.map((o) => {
              const checked = filters.makes.includes(o.value);
              return (
                <button key={o.value} onClick={() => toggle("makes", o.value)} data-testid={`${prefix}filter-makes-${o.value.toLowerCase().replace(/\s/g, "-")}`}
                  className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm transition-colors ${checked ? "bg-rose-50 font-semibold text-rose-600" : "text-slate-700 dark:text-slate-300 hover:bg-slate-100"}`}>
                  <span className="flex items-center gap-2"><span className={`flex h-4 w-4 items-center justify-center rounded border ${checked ? "border-rose-600 bg-rose-600" : "border-slate-300 dark:border-slate-700"}`}>{checked && <span className="h-1.5 w-1.5 rounded-sm bg-white dark:bg-slate-900" />}</span>{o.value}</span>
                  <span className="text-xs text-slate-400 dark:text-slate-500">{o.count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {filters.makes.length > 0 && facets.models.length > 0 && <FacetGroup title="Model" options={facets.models} keyName="models" prefix={prefix} />}
        {filters.models.length > 0 && facets.trims.length > 0 && <FacetGroup title="Trim" options={facets.trims} keyName="trims" prefix={prefix} />}
        {facets.bodies.length > 0 && (
          <div className="border-b border-slate-100 dark:border-slate-800 py-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Body type</p>
            <div className="flex flex-wrap gap-2">
              {facets.bodies.map((o) => {
                const checked = filters.bodies.includes(o.value);
                const Icon = BODY_ICON[o.value] || Car;
                return (
                  <button key={o.value} onClick={() => toggle("bodies", o.value)} data-testid={`${prefix}filter-bodies-${o.value.toLowerCase()}`}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${checked ? "border-rose-600 bg-rose-50 text-rose-600" : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:border-slate-300"}`}>
                    <Icon className="h-4 w-4" strokeWidth={2} /> {o.value} <span className="text-slate-400 dark:text-slate-500">{o.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {facets.colors.length > 0 && (
          <div className="border-b border-slate-100 dark:border-slate-800 py-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Color</p>
            <div className="flex flex-wrap gap-2">
              {facets.colors.map((o) => {
                const checked = filters.colors.includes(o.value);
                return (
                  <button key={o.value} onClick={() => toggle("colors", o.value)} title={`${o.value} (${o.count})`} data-testid={`${prefix}filter-colors-${o.value.toLowerCase()}`}
                    className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition-all ${checked ? "border-rose-600 ring-1 ring-rose-600" : "border-slate-200 dark:border-slate-800 hover:border-slate-300"}`}>
                    <span className="h-3.5 w-3.5 rounded-full border border-slate-300 dark:border-slate-700" style={{ background: COLOR_HEX[o.value] || "#e2e8f0" }} />{o.value}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {yb.min != null && (
          <div className="border-b border-slate-100 dark:border-slate-800 py-4">
            <div className="mb-1 flex items-center justify-between"><p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Year</p><p className="text-xs font-semibold text-slate-600 dark:text-slate-300">{yearVal[0]}–{yearVal[1]}</p></div>
            <RangeSlider min={yb.min} max={yb.max} step={1} value={yearVal} onValueChange={(v) => set({ yearMin: v[0], yearMax: v[1] })} onValueCommit={(v) => set({ yearMin: v[0], yearMax: v[1] })} />
          </div>
        )}
        {pb.min != null && (
          <div className="py-4">
            <div className="mb-1 flex items-center justify-between"><p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Price</p><p className="text-xs font-semibold text-slate-600 dark:text-slate-300">{currency(priceVal[0])}–{currency(priceVal[1])}</p></div>
            <RangeSlider min={pb.min} max={pb.max} step={1000} value={priceVal} onValueChange={(v) => set({ priceMin: v[0], priceMax: v[1] })} onValueCommit={(v) => set({ priceMin: v[0], priceMax: v[1] })} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <section className="relative overflow-hidden border-b border-slate-800 bg-slate-950 text-white">
        <div className="ab-hero-grid absolute inset-0 opacity-30" />
        <div className="ab-glow absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:py-16">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-rose-400">{c.inventory_eyebrow}</p>
          <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">{c.inventory_heading}</h1>
          <p className="mt-3 max-w-2xl text-slate-300">{c.inventory_subtext}</p>
        </div>
      </section>
      <PromotionBannerCarousel promotions={promos} />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <FeaturedTextPromotions promotions={promos} />
        <div className="flex flex-wrap items-center gap-3">
          <form onSubmit={(e) => e.preventDefault()} className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <Input data-testid="inventory-search" type="search" aria-label="Search inventory" value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search make, model, trim…" className="pl-9" />
          </form>
          <select data-testid="sort-select" value={filters.sort} onChange={(e) => set({ sort: e.target.value })} className="h-10 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm font-semibold text-slate-700 dark:text-slate-300">
            <option value="newest">Newest</option>
            <option value="price_asc">Price: low to high</option>
            <option value="price_desc">Price: high to low</option>
            <option value="year_desc">Year: newest</option>
          </select>
          <Button variant="outline" onClick={shareResults} className="rounded-md border-slate-300 dark:border-slate-700 font-semibold" data-testid="share-results-btn"><Share2 className="mr-1.5 h-4 w-4" /> Share</Button>
          {promoCount > 0 && (
            <Button variant="outline" onClick={() => setSpecials((s) => !s)} data-testid="specials-toggle"
              className={`rounded-md font-semibold ${specials ? "border-rose-600 bg-rose-50 text-rose-600" : "border-slate-300 dark:border-slate-700"}`}>
              <Tag className="mr-1.5 h-4 w-4" /> Specials <span className="ml-1.5 rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] text-white">{promoCount}</span>
            </Button>
          )}
          {isBuyer && <Button variant="outline" onClick={saveSearch} className="rounded-md border-slate-300 dark:border-slate-700 font-semibold" data-testid="save-search-btn"><BookmarkPlus className="mr-1.5 h-4 w-4" /> Save search</Button>}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="rounded-md border-slate-300 dark:border-slate-700 font-semibold lg:hidden" data-testid="mobile-filters-button"><SlidersHorizontal className="mr-1.5 h-4 w-4" /> Filters</Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto" aria-describedby={undefined}>
              <SheetTitle className="sr-only">Filters</SheetTitle>
              <p className="mt-4 font-display text-lg font-bold text-slate-900 dark:text-slate-100">Filters</p>
              {renderFilters("m-")}
            </SheetContent>
          </Sheet>
        </div>

        {anyActive ? (
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="active-filters">
            {filters.q && <Chip label={`"${filters.q}"`} onRemove={() => { setQInput(""); set({ q: "" }); }} />}
            {activeChips.map((c) => <Chip key={c.k + c.v} label={c.label} onRemove={() => toggle(c.k, c.v)} />)}
            {filters.verified && <Chip label="Verified" onRemove={() => set({ verified: false })} />}
            {filters.dealerListed && <Chip label="Dealer Listed" onRemove={() => set({ dealerListed: false })} />}
            {filters.yearMin != null && <Chip label={`${filters.yearMin}–${filters.yearMax}`} onRemove={() => set({ yearMin: null, yearMax: null })} />}
            {filters.priceMin != null && <Chip label={`${currency(filters.priceMin)}–${currency(filters.priceMax)}`} onRemove={() => set({ priceMin: null, priceMax: null })} />}
            <button onClick={clearAll} data-testid="clear-filters" className="text-xs font-bold text-rose-600 hover:underline">Clear all</button>
          </div>
        ) : null}

        <div className="mt-6 flex gap-8">
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-20 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 shadow-sm">
              <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 py-3">
                <SlidersHorizontal className="h-4 w-4 text-rose-600" />
                <span className="font-display text-sm font-bold uppercase tracking-wide text-slate-900 dark:text-slate-100">Filters</span>
              </div>
              {renderFilters("")}
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <p className="mb-4 text-sm font-semibold text-slate-500 dark:text-slate-400" data-testid="result-count">{vehicles === null ? "Loading…" : `${shown.length} vehicle${shown.length !== 1 ? "s" : ""}${specials ? " on special" : ""}`}</p>
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {vehicles === null && Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-0"><Skeleton className="aspect-[16/10] w-full" /><div className="space-y-2 p-5"><Skeleton className="h-4 w-24" /><Skeleton className="h-5 w-40" /></div></div>
              ))}
              {shown && shown.map((v) => <VehicleCard key={v.id} v={v} matched={matchedIds.has(v.id)} promo={promoForVehicle(promos, v)} />)}
            </div>
            {vehicles && vehicles.length === 0 && (
              <div className="mt-6 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-12 text-center" data-testid="inventory-empty">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600"><Sparkles className="h-6 w-6" /></div>
                <p className="mt-4 font-display text-xl font-bold text-slate-900 dark:text-slate-100">Not in stock — but we can still find it</p>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Our brokers source almost any make and model across California. Tell us what you're after.</p>
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  <Button data-testid="empty-concierge-btn" onClick={() => navigate("/concierge", { state: { brands: filters.makes.join(", "), bodies: filters.bodies } })} className="rounded-md bg-slate-950 font-semibold hover:bg-slate-800"><Sparkles className="mr-1.5 h-4 w-4" /> Have us source it</Button>
                  <Button variant="outline" onClick={clearAll} className="rounded-md border-slate-300 dark:border-slate-700 font-semibold" data-testid="empty-clear-btn">Clear filters</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

const Chip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:text-slate-300">
    {label}
    <button onClick={onRemove} className="text-slate-400 dark:text-slate-500 hover:text-slate-700"><X className="h-3 w-3" /></button>
  </span>
);
